import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { isNative, originUrl, openExternal, saveAndShare } from "@/lib/native";
import { getAccess, getRefresh, saveTokens } from "@/lib/tokens";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((cfg) => {
  if (isNative) {
    cfg.headers["X-Client"] = "native";
    const t = getAccess();
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

export async function refreshSession() {
  if (!isNative) return api.post("/auth/refresh");
  const rt = await getRefresh();
  if (!rt) throw new Error("No session");
  const { data } = await api.post("/auth/refresh", { refresh_token: rt });
  await saveTokens(data);
  return data;
}

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const orig = error.config;
    if (error.response?.status === 401 && orig && !orig._retry && !/\/auth\/(login|register|refresh)/.test(orig.url)) {
      orig._retry = true;
      try {
        refreshing = refreshing || refreshSession();
        await refreshing;
        refreshing = null;
        return api(orig);
      } catch (e) {
        refreshing = null;
      }
    }
    return Promise.reject(error);
  }
);

export function formatErr(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" ");
  return detail.msg || String(detail);
}

export const photoSrc = (c) => (c?.photo_path ? `${API}/files/${c.photo_path}` : c?.photo_url || "");

export const isMember = (u) => u?.role === "admin" || u?.membership?.status === "active";

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export const fmtDay = (s) => (s ? s.slice(0, 10).split("-").reverse().join("/") : "");

export function useFetch(url, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ...deps]);
  useEffect(() => { load(); }, [load]);
  return { data, setData, loading, reload: load };
}

export function useShortlist() {
  const [ids, setIds] = useState([]);
  useEffect(() => {
    api.get("/shortlist").then((r) => setIds(r.data.map((c) => c.id))).catch(() => {});
  }, []);
  const toggle = async (id) => {
    const on = ids.includes(id);
    setIds(on ? ids.filter((x) => x !== id) : [...ids, id]);
    try {
      on ? await api.delete(`/shortlist/${id}`) : await api.post(`/shortlist/${id}`);
      toast.success(on ? "Removed from shortlist" : "Added to shortlist");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return { ids, toggle };
}

export async function downloadBlob(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  if (isNative) return saveAndShare(res.data, filename);
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

export const downloadDoc = (d) => downloadBlob(`/documents/${d.id}/download`, d.filename).catch((e) => toast.error(formatErr(e)));

export async function startCheckout(planId) {
  try {
    const { data } = await api.post("/payments/checkout", { plan_id: planId, origin_url: originUrl() });
    await openExternal(data.checkout_url);
  } catch (e) {
    toast.error(formatErr(e));
  }
}
